/**
 * 待审核词汇（旧 views/dynamic-lexemes/PendingReviewView.vue）：单个和批量批准 / 拒绝
 */
import { useEffect, useState } from 'react'
import { Alert, App, Badge, Button, Card, Space, Table, Tag, Typography, type TableColumnsType } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import { DynamicLexemeStatus, type DynamicLexeme } from '@/types/dynamic-lexemes'
import { useLexemeReviewList, useReviewLexemes } from './hooks'
import styles from './DynamicLexemes.module.css'

type ReviewAction = DynamicLexemeStatus.APPROVED | DynamicLexemeStatus.REJECTED

const ACTION_TEXT: Record<ReviewAction, string> = {
  [DynamicLexemeStatus.APPROVED]: '批准',
  [DynamicLexemeStatus.REJECTED]: '拒绝'
}

export function PendingReviewPage() {
  const { message } = App.useApp()
  const { data: lexemes = [], isFetching, isError, refetch } = useLexemeReviewList(50)
  const review = useReviewLexemes()
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  useEffect(() => {
    if (isError) message.error('获取待审核词汇失败')
  }, [isError, message])

  const refresh = async () => {
    setSelectedIds([])
    const result = await refetch()
    if (!result.isError) message.success('数据已刷新')
  }

  const reviewOne = async (record: DynamicLexeme, action: ReviewAction) => {
    const text = ACTION_TEXT[action]
    try {
      await review.mutateAsync({
        lexeme_ids: [record.lexeme_id],
        action,
        review_notes: `单个${text}`,
        batch_action: false
      })
      message.success(`词汇 "${record.word}" 已${text}`)
    } catch (error) {
      console.error(`${text}失败:`, error)
      message.error(`${text}失败`)
    }
  }

  const reviewSelected = async (action: ReviewAction) => {
    const text = ACTION_TEXT[action]
    try {
      await review.mutateAsync({
        lexeme_ids: selectedIds,
        action,
        review_notes: `批量${text}`,
        batch_action: true
      })
      message.success(`已批量${text} ${selectedIds.length} 个词汇`)
      setSelectedIds([])
    } catch (error) {
      console.error(`批量${text}失败:`, error)
      message.error(`批量${text}失败`)
    }
  }

  const columns: TableColumnsType<DynamicLexeme> = [
    {
      title: '词汇',
      key: 'word',
      width: 120,
      render: (_, record) => (
        <Tag color="blue" className={styles.wordTag}>
          {record.word}
        </Tag>
      )
    },
    { title: '语言', dataIndex: 'language', key: 'language', width: 80 },
    {
      title: '创建上下文',
      key: 'context',
      width: 200,
      render: (_, record) => (
        <Typography.Paragraph className={styles.context} ellipsis={{ rows: 2, expandable: true }}>
          {record.metadata.created_from_context || '无上下文'}
        </Typography.Paragraph>
      )
    },
    {
      title: '遇到情况',
      key: 'encounters',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tag color="green">{record.metadata.encounter_count}次</Tag>
          <Tag color="blue">{record.metadata.user_count}人</Tag>
        </Space>
      )
    },
    {
      title: '置信度',
      key: 'confidence',
      width: 100,
      render: (_, record) => `${Math.round(record.metadata.confidence_score * 100)}%`
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" onClick={() => reviewOne(record, DynamicLexemeStatus.APPROVED)}>
            批准
          </Button>
          <Button danger size="small" onClick={() => reviewOne(record, DynamicLexemeStatus.REJECTED)}>
            拒绝
          </Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader title="待审核词汇" subtitle="管理用户遇到的新词汇，决定是否加入系统词库" />

      <Card
        title={
          <>
            待审核词汇列表
            <Badge className={styles.countBadge} count={lexemes.length} color="orange" />
          </>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => void refresh()}>
            刷新
          </Button>
        }
      >
        <Table<DynamicLexeme>
          // 旧版没设 rowKey，选中的是行下标；这里用词汇 id，批量操作提交的才是真正的 id
          rowKey="lexeme_id"
          columns={columns}
          dataSource={lexemes}
          loading={isFetching}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showQuickJumper: true }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as number[])
          }}
        />

        {selectedIds.length > 0 && (
          <Alert
            className={styles.batchBar}
            type="info"
            showIcon
            title={`已选择 ${selectedIds.length} 个词汇`}
            action={
              <Space>
                <Button type="primary" loading={review.isPending} onClick={() => reviewSelected(DynamicLexemeStatus.APPROVED)}>
                  批量批准
                </Button>
                <Button danger loading={review.isPending} onClick={() => reviewSelected(DynamicLexemeStatus.REJECTED)}>
                  批量拒绝
                </Button>
              </Space>
            }
          />
        )}
      </Card>
    </div>
  )
}
